import { IAgoraRTCClient, IRemoteVideoTrack } from 'agora-rtc-sdk-ng';
import { FirebaseApp } from 'firebase/app';
import { Database } from 'firebase/database';
import { ReactElement } from 'react';
// `import type` deliberately: utils/importMapping imports the InputComponents
// enum (a runtime value) from this file, so a value import here would close a
// circular require. This one is erased at compile time.
import type { WireImportMapping } from 'utils/importMapping';
import type {
  ConsentReportParams,
  LegalConsentSettings,
  LegalSlug,
  LoginConsents,
  PublishLegalDocumentPayload,
  RecordSmsConsentPayload
} from 'types/legal';

// ==============================|| TYPES - AUTH  ||============================== //

export type GuardProps = {
  children: ReactElement | null;
};

export enum LoginType {
  USER_NAME = 'USER_NAME',
  MOBILE = 'MOBILE',
  RESERVED = 'RESERVED',
  ENTRA_ID = 'ENTRA_ID',
  MICROSOFT_OAUTH = 'MICROSOFT_OAUTH'
}

export type UserProfile = {
  uid: string;
  id?: string;
  email?: string;
  avatar?: string;
  image?: string;
  name?: string;
  role?: string;
  tier?: string;
};

export interface AuthProps {
  isLoggedIn: boolean;
  isInitialized?: boolean;
  isLoginInitiated?: boolean;
  user?: any | null;
  token?: string | null;
}

export interface AuthActionProps {
  type: string;
  payload?: AuthProps;
}

export interface InitialLoginContextProps {
  isLoggedIn: boolean;
  isInitialized?: boolean;
  user?: UserProfile | null | undefined;
}

export interface JWTDataProps {
  userId: string;
}

export enum RoleType {
  SYS_ADMIN = 'SYS_ADMIN',
  ANONYMOUS = 'ANONYMOUS',
  CLIENT_ADMIN = 'CLIENT_ADMIN',
  TEAM_MANAGER = 'TEAM_MANAGER',
  CENTRAL_SUPPORT_EXECUTIVE = 'CENTRAL_SUPPORT_EXECUTIVE',
  FIELD_SUPPORT_EXECUTIVE = 'FIELD_SUPPORT_EXECUTIVE'
}

// ==============================|| TYPES - ANNOUNCEMENTS ||============================== //

// What kind of post an announcement is. A label and a list filter — it has no bearing on
// who the announcement reaches or how read state is tracked. Mirrors the server's
// AnnouncementType enum; the wire values are lowercase.
export type AnnouncementKind = 'announcement' | 'training';

// Labels for the chip and the form, kept next to the type so the two cannot drift.
export const ANNOUNCEMENT_KIND_LABELS: Record<AnnouncementKind, string> = {
  announcement: 'Announcement',
  training: 'Training'
};

// What `uploadAsset` hands back, trimmed to the fields the API persists. The signed `url`
// that upload also returns is deliberately not sent — it is short-lived, and the server
// re-mints it on every read.
export interface AnnouncementAttachmentInput {
  file_name: string;
  // True for a link the author pasted rather than a file we hold. Then `public_url` is
  // the address, `file_path`/`mime_type` are empty, and `media_type` must be supplied —
  // there is no mime type for the server to infer one from.
  is_link?: boolean;
  file_path?: string | null;
  mime_type?: string | null;
  public_url?: string | null;
  media_type?: 'image' | 'video' | 'file';
  file_size?: number | null;
}

export interface AnnouncementAttachment extends AnnouncementAttachmentInput {
  // For an upload, derived server-side from the mime prefix; for a link, the author's
  // choice. Tells the UI whether to render <img>, <video controls>, or a link card.
  media_type: 'image' | 'video' | 'file';
  // Freshly signed on every read; for a link it is just `public_url` passed through.
  url?: string | null;
}

// `clients`, `process` and `roles` all come back populated with just these two fields.
export interface AnnouncementClientRef {
  uid: string;
  name: string;
}

export interface Announcement {
  uid: string;
  title: string;
  description: string;
  // Server-defaulted, so records written before the field existed read back as
  // 'announcement' rather than undefined.
  type: AnnouncementKind;
  clients: AnnouncementClientRef[];
  // Broadcast — reaches clients onboarded after the announcement was posted.
  // `clients` is empty whenever this is true.
  allClients: boolean;
  // The audience funnel's lower two levels, set together or not at all. `process` is a
  // Teams document and `roles` are user ResourceTypes belonging to the same client.
  //
  // Both are only ever populated alongside exactly one entry in `clients`. Null and empty
  // mean the announcement is client-wide, which is also how every record written before
  // this feature reads back.
  process?: AnnouncementClientRef | null;
  roles?: AnnouncementClientRef[];
  attachments: AnnouncementAttachment[];
  status: boolean;
  createdBy?: string | null;
  updatedBy?: string | null;
  // Doubles as the release date shown on each card.
  createdAt: string;
  updatedAt: string;
  // Per-caller read state, decided server-side. Not derivable here: reads happen one
  // announcement at a time and in any order, so there is no client-side timestamp
  // comparison that reproduces it. Always true for a Super Admin.
  isRead: boolean;
}

export interface AnnouncementListData {
  list: Announcement[];
  total: number;
  page: number;
  size: number;
  hasMore: boolean;
}

export interface AnnouncementVisibility {
  visible: boolean;
  // null for a Super Admin — they always see the tab, so no count is computed.
  count: number | null;
  // Sent by the server rather than inferred from `count === null`, which was only ever
  // a proxy for it.
  isSuperAdmin: boolean;
  // Drives the nav badge. Drops by one per announcement opened; 0 for a Super Admin.
  unreadCount: number;
  // The read watermark — everything created at or before it counts as read.
  lastSeenAt: string | null;
}

export interface AnnouncementListParams {
  page?: number;
  size?: number;
  keyword?: string;
  // Narrows the feed to one kind. Omit for both — there is no 'all' value on the wire,
  // an absent param is what means "no filter".
  type?: AnnouncementKind;
  // Client UID. Super Admin only — the server ignores it for anyone else.
  client?: string;
}

export interface CreateAnnouncementPayload {
  title: string;
  description: string;
  // Optional on the wire — the server defaults it to 'announcement'.
  type?: AnnouncementKind;
  // Client UIDs, resolved to ids server-side. Ignored when `allClients` is true,
  // and required to be non-empty otherwise.
  clients?: string[];
  allClients?: boolean;
  // Teams UID, and user ResourceType UIDs within that same client. Only accepted when
  // `clients` holds exactly one entry and `allClients` is false; `process` and a non-empty
  // `roles` must be sent together. The server re-checks that both belong to the selected
  // client and rejects the save otherwise.
  process?: string | null;
  roles?: string[];
  attachments?: AnnouncementAttachmentInput[];
}

// Partial update. The server leaves the audience untouched unless the payload mentions it
// — but if it mentions *any* of clients / allClients / process / roles, all four are
// rewritten together. Send the audience whole or not at all; a half-update would leave the
// process pointing at the previous client.
export type UpdateAnnouncementPayload = Partial<CreateAnnouncementPayload>;

// What the "this will reach N people" line shows. `count` is null for a client-wide or
// broadcast audience, whose membership is not a fixed number worth quoting.
export interface AnnouncementAudienceCount {
  count: number | null;
}

export type ApiContextType = {
  getPriorityTransactions: (payload: any) => Promise<any>;
  saveTeamNotifications: (teamUid: string, notifications: any) => Promise<any>;
  isLoggedIn: boolean;
  isLoginInitiated?: boolean;
  isInitialized?: boolean;
  user?: any;
  // 'idle' and 'expired' make the login page say why the user was signed out.
  logout: (reason?: 'logout' | 'idle' | 'expired') => Promise<void>;
  // Rejects with a ConsentRequiredError (types/legal) on a 428 CONSENT_REQUIRED.
  login: (email: string, password: string, consents?: LoginConsents) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  resetPassword: (email: string,domainUrl:string) => Promise<void>;
  resetPasswordWithToken: (token: string, password: string) => Promise<any>;
  updateProfile: VoidFunction;
  getTeamNotifications: (teamUid: string) => Promise<any>;
  deleteTeamNotification : (teamUid: string, notificationUid: string) => Promise<any>;
  updateTeamNotification: (teamUid: string, notificationUid: string, payload: any) => Promise<any>;
  uploadAsset: (file: File, path: string) => Promise<any>;
  getAssetSignedUrl: (file_path: string, file_name?: string) => Promise<any>;
  createClient: (payload: any) => Promise<any>;
  createTeam: (payload: any) => Promise<any>;
  updateClient: (payload: any, uid: string) => Promise<any>;
  updateTeam: (payload: any, uid: string) => Promise<any>;
  updateChildTxnStartStep: (uid: string, childTxnStartStep: string | null) => Promise<any>;
  showToast: (message: string, variant: 'default' | 'error' | 'success' | 'warning' | 'info', config?: any, anchorOptions?: any) => void;
  getClients: (payload: { page: number; size: number; keyword?: string; status?: 'ACTIVE' | 'INACTIVE' }) => Promise<any>;
  getClientByUid: (uid: string) => Promise<any>;
  getTeamByUid: (uid: string) => Promise<any>;
  manageTeamLabels: (uid: string, payload: any) => Promise<any>;
  base64Decode: (str: string) => string;
  getRolesWithFilter: (filters: any) => Promise<any>;
  editUser: (uid: string, payload: any) => Promise<any>;
  inviteUser: (payload: any) => Promise<any>;
  getUsers: (payload: any) => Promise<any>;
  activateOrDeactivate: (payload: any, uid: string) => Promise<any>;
  getTeams: (payload: { page: number; size: number; keyword?: string; clients?: string[] }) => Promise<any>;
  getClientsList: () => Promise<any>;
  getTeamTags: (teamUid: string, audience?: string) => Promise<any>;
  createTag: (uid: string, payload: any) => Promise<any>;
  manageTag: (teamUid: string, tagUid: string, payload: any) => Promise<any>;
  getUsersByClient: (clientUid: string) => Promise<any>;
  manageTeamUsers: (teamUid: string, payload: any) => Promise<any>;
  getTeamUsers: (teamUid: string) => Promise<any>;
  removeTeamUser: (teamUid: string, userUid: string) => Promise<any>;
  answerCall: (uid: string, eventStatus: CallStatus) => Promise<any>;
  initiateCallToUser: (
    transactionUid: string,
    opts?: { stepUid?: string; targetUserUids?: string[]; sessionType?: string }
  ) => Promise<any>;
  cancelCall: (uid: string) => Promise<any>;
  getCallCandidates: (transactionUid: string) => Promise<any>;
  uploadScreenshot: (uid: string, screenshot: any) => Promise<any>;
  startSessionRecording: (uid: string, payload: { uid: string; path: string }) => Promise<any>;
  stopSessionRecording: (uid: string, payload: { uid: string }) => Promise<any>;
  endSession: (uid: string) => Promise<any>;
  updateSessionUserName: (uid: string, userName: string) => Promise<any>;
  supportReview: (uid: string, payload: { rating: number; comment: string }) => Promise<any>;
  getSupportUsers: (uid: string) => Promise<any>;
  inviteSupportUser: (uid: string, userUid: string) => Promise<any>;
  getDashboardCount: (payload: { start: string; end: string }) => Promise<any>;
  getDashboardCallLogs: (payload: { start: string; end: string; status: CallStatus }) => Promise<any>;
  getFlowByTeamUid: (teamUid: string) => Promise<any>;
  getImportMapping: (teamUid: string) => Promise<any>;
  saveImportMapping: (teamUid: string, payload: WireImportMapping) => Promise<any>;
  replicateTeamByUid: (
    teamUid: string,
    payload: { name: string; newTeamName?: string; teamName?: string }
  ) => Promise<any>;
  getTeamDetailsByUid: (teamUid: string) => Promise<any>;
  updateNotificationStatus: (id: string, status: boolean) => Promise<any>;
  createHidingField: (teamUid: string, payload: any) => Promise<any>;
  getAllHidingFields: (teamUid: string) => Promise<any>;
  updateHidingField: (id: any, teamUid: any, payload: any) => Promise<any>;
  deleteHidingField: (id: string) => Promise<any>;
  createAssignmentMessagingConfig: (teamUid: string, payload: any) => Promise<any>;
  getAssignmentMessagingConfigs: (teamUid: string) => Promise<any>;
  updateAssignmentMessagingConfig: (id: any, teamUid: any, payload: any) => Promise<any>;
  deleteAssignmentMessagingConfig: (id: string) => Promise<any>;
  getUserRoleAll: () => Promise<any>;
  editTransactionDetails:(keyId:string)=>Promise<any>;
  addNewStep: (
    // 👈 this
    teamUid: string,
    payload: {
      currentNode: {
        title: string;
        stepType: string;
        parents: string[];
        dependency: string[];
      };
      parentNode: {
        nodeKey: 'onCompleteStep' | 'onFailedStep';
      };
    }
  ) => Promise<any>;

  addStepAndConnect: (
    teamUid: string,
    payload: {
      currentNode: {
        title: string;
        stepType: string;
        position: { x: number; y: number };
        parents: string[];
        dependency: string[];
      };
      parentNode: {
        nodeKey: 'onCompleteStep' | 'onFailedStep';
      };
      insertBetween: Array<{
        parentStepUid: string;
        childStepUid: string;
        edgeType: 'onCompleteStep' | 'onFailedStep';
      }>;
      updatedBy?: string;
      notificationConfig?: any;
    }
  ) => Promise<any>;

  updateFlowDiagram: (
    teamUid: string,
    payload: {
      steps: {
        id: string;
        parents: string[];
        onCompleteStep: string[];
        onFailedStep: string[];
        stepType: string;
        reviewers: null;
        actor: null;
        dependency: string[];
        diagramData: null;
        onInProgress: object;
        onComplete: object;
        onFailure: object;
      }[];
      diagramData: {
        id: string;
        position: { x: number; y: number };
        measured?: any;
        type?: string;
      }[];
    }
  ) => Promise<any>;

  deleteStepByStepUid: (teamUid: string, stepUid: string) => Promise<any>;
  checkDeleteDependencies: (
    teamUid: string,
    options: { fieldUid?: string; stepUid?: string }
  ) => Promise<any>;

  transactionWithFilter: (payload: any) => Promise<any>;
  transactionOrder: (payload: any, options?: { signal?: AbortSignal }) => Promise<any>;
  transactionKanban: (payload: any, options?: { signal?: AbortSignal }) => Promise<any>;
  transactionOrderWithHistory: (payload: any) => Promise<any>;
  getTransactionHistory: (uid: string) => Promise<any>;
  getTransactionDetails: (uid: string) => Promise<any>;
  getUnmappedTransaction: (payload: any) => Promise<any>;
  getDashboardTags: () => Promise<any>;
  getTeamByClient: (uid: string) => Promise<any>;
  mapKeyToTeam: (payload: any) => Promise<any>;
  submitSSODetails: (payload: any, uid: string) => Promise<any>;
  getSSODetails: (uid: string) => Promise<any>;
  getDocuments: (entityId: string, entityType: string) => Promise<any>;
  deleteDocument:(documentId:string)=>Promise<any>;
  uploadDocument: (file: File, entityId: string, entityType: string) => Promise<any>;
  getNodeStatus: (uid: string) => Promise<any>;
  addNewStepFlow: (uid: string) => Promise<any>;
  createNodeStatus: (payload: { name: string }, uid: string) => Promise<any>;
  updateNodeStatus: (payload: { name: string }, statusUid: string) => Promise<any>;
  getNodeReports: (uid: string) => Promise<any>;
  createNodeReport: (payload: { name: string }, uid: string) => Promise<any>;
  updateNodeReport: (payload: { name: string }, reportUid: string) => Promise<any>;
  deleteNodeReport: (reportUid: string) => Promise<any>;
  getTransactionHistoryDetails: (uid: string) => Promise<any>;
  getTransactionHistoryCore: (uid: string) => Promise<any>;
  getTransactionHistorySummary: (uid: string) => Promise<any>;
  getTransactionHistoryTimeline: (
    uid: string,
    options?: { before?: string; after?: string; limit?: number }
  ) => Promise<any>;
  deleteTransaction: (transactionKey: string) => Promise<any>;
  updateTransactionHistoryStep: (historyId: string, payload: any) => Promise<any>;
  redoStep: (uid: string, payload: any) => Promise<any>;
  resetToStep: (uid: string, stepUid: string) => Promise<any>;
  resetToStepByRole: (uid: string, stepUid: string, resourceTypeUid: string) => Promise<any>;
  getFormData: (key: string) => Promise<any>;
  uploadEventData: (payload: any) => Promise<any>;
  // Client roles methods
  getClientRoles: (clientUid: string) => Promise<any>;
  assignRoleToClient: (clientUid: string, payload: any) => Promise<any>;
  updateClientRole: (clientUid: string, roleUid: string, payload: any) => Promise<any>;
  getPermissions: () => Promise<any>;
  // Microsoft OAuth methods
  microsoftLogin: (consents?: LoginConsents) => Promise<void>;
  getMicrosoftAccount: () => any | null;  isMicrosoftAuthenticated: () => boolean;
  completeProfile: (data: any) => Promise<any>;
  registerFCMToken: (payload: DeviceRegistrationPayload) => Promise<any>;
  microsoftLoading: boolean;

  // Resources Management APIs
  // Second argument is the resource-type `category`, not a user — the declaration said
  // `user?: any` but the implementation has always passed it through as `category`.
  // 'user' narrows the result to roles that describe people; the directory also models
  // vehicles and similar, which are not targetable audiences.
  // `null` for no category filter — several callers pass it explicitly.
  getResourceTypes: (clientUID?: string, category?: 'user' | 'others' | null) => Promise<any>;

  // Detection targets: the classes the mobile capture gate can recognise. Served
  // from the backend so adding one is an upload, not an app release.
  getDetectionTargets: () => Promise<any>;
  getDetectionTarget: (name: string) => Promise<any>;
  saveDetectionTarget: (name: string, payload: any) => Promise<any>;
  deleteDetectionTarget: (name: string) => Promise<any>;
  createResourceType: (payload: {
    name: string;
    description: string;
    fields: Array<{
      name: string;
      type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
      required: boolean;
      dropdownSource?: 'teams' | 'countryCode';
      dropdownOptions?: string[];
    }>;
    clientUID?: string;
    category?: 'user' | 'others';
    access?: string[];
    invite?: boolean;
    allowRating?: boolean;
    restrictTransactionsToAssigned?: boolean;
  }) => Promise<any>;
  updateResourceType: (id: string, payload: {
    name: string;
    description: string;
    fields: Array<{
      name: string;
      type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
      required: boolean;
      dropdownSource?: 'teams' | 'countryCode';
      dropdownOptions?: string[];
    }>;
    clientUID?: string;
    category?: 'user' | 'others';
    access?: string[];
    invite?: boolean;
    allowRating?: boolean;
    restrictTransactionsToAssigned?: boolean;
  }) => Promise<any>;
  deleteResourceType: (id: string) => Promise<any>;
  getResourceData: (resourceTypeId: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
    clientUID?: string;
  }) => Promise<any>;
  getAllResourceData: (params?: {
    resourceTypeId?: string;
    page?: number;
    limit?: number;
    search?: string;
    clientUID?: string;
  }, options?: { signal?: AbortSignal }) => Promise<any>;
  createResourceData: (resourceTypeId: string, data: { [key: string]: any }, additionalPayload?: { [key: string]: any }) => Promise<any>;
  updateResourceData: (id: string, payload: {
    resourceTypeId: string;
    data: { [key: string]: any };
    clientUID?: string;
  }, additionalPayload?: { [key: string]: any }) => Promise<any>;
  deleteResourceData: (id: string) => Promise<any>;
  searchResources: (resourceType: string | null, query: string, resourceTypes?: string[], clientUID?: string) => Promise<any>;
  getResourceTypeById: (resourceTypeId: string) => Promise<any>;
  getResourceDataById: (resourceDataId: string) => Promise<any>;
  getResourceDataByUid: (resourceDataUid: string) => Promise<any>;
  getLastLoginBatch: (uids: string[]) => Promise<any>;
  getVerificationStatusBatch: (uids: string[]) => Promise<any>;
  retryAiDetection: (eventId: string, configId?: string) => Promise<any>;
  resolveDetectionResult: (
    eventId: string,
    configId: string,
    outcome: 'true' | 'false',
    reason: string,
  ) => Promise<any>;
  clearDetectionResolution: (eventId: string, configId: string) => Promise<any>;
  getDetectionProviders: () => Promise<any>;
  draftDetectionRule: (teamUid: string, request: string, steps: any[], resourceTypes: any[]) => Promise<any>;
  previewDetectionCandidates: (teamUid: string, rule: any, limit?: number) => Promise<any>;
  previewDetectionRule: (teamUid: string, rule: any, transactionUid: string) => Promise<any>;
  backtestDetectionRule: (teamUid: string, rule: any, limit?: number) => Promise<any>;
  publishDetectionConfig: (teamUid: string, configUid: string) => Promise<any>;
  archiveDetectionConfig: (teamUid: string, configUid: string) => Promise<any>;
  stopDriverTracking: (transactionUid: string, driverUid: string) => Promise<any>;

  // Resource Rating APIs
  getResourceTransactionsWithRatings: (resourceId: string) => Promise<any>;
  rateResource: (payload: {
    resourceId: string;
    transactionId: string;
    rating: 'good' | 'bad' | 'none';
    comment?: string;
    userId: string;
  }) => Promise<any>;
  
  // Rule Engine APIs
  createRuleByStep: (stepUid: string, rulePayload: any) => Promise<any>;
  updateRule: (ruleUid: string, rulePayload: any) => Promise<any>;
  getRulesByStep: (stepUid: string) => Promise<any>;
  getAllRules: () => Promise<any>;
  getRuleByUid: (ruleUid: string) => Promise<any>;
  getRulesByTeamUid: (teamUid: string, isStepRule?: boolean) => Promise<any>;
  deleteRule: (ruleUid: string) => Promise<any>;
  deleteNodeStatus:(uid:string)=>Promise<any>;
  // Chat APIs
  createChatThread: (payload: {
    transactionId: string;
    subject?: string;
    participants: string[];
    initialMessage?: string;
    initialAttachments?: Array<{
      fileName: string;
      filePath: string;
      mimeType: string;
      publicUrl: string;
      fileSize?: number;
    }>;
    initialMentions?: Array<{
      uid: string;
      name: string;
    }>;
  }) => Promise<any>;

  getChatThreadsByTransaction: (transactionId: string) => Promise<any>;
  getChatParticipants: (transactionId: string) => Promise<any>;
  getChatThreadDetails: (threadUid: string) => Promise<any>;
  sendChatMessage: (threadUid: string, payload: {
    message: string;
    messageType?: 'TEXT' | 'IMAGE' | 'FILE' | 'SYSTEM';
    attachments?: Array<{
      fileName: string;
      filePath: string;
      mimeType: string;
      publicUrl: string;
      fileSize?: number;
    }>;
    mentions?: Array<{
      uid: string;
      name: string;
    }>;
  }) => Promise<any>;
  getChatMessages: (threadUid: string, page?: number, limit?: number) => Promise<any>;
  markChatMessagesAsRead: (threadUid: string, payload?: {
    messageIds?: string[];
    markAll?: boolean;
  }) => Promise<any>;
  markAllChatsAsRead: () => Promise<{
    success: boolean;
    message: string;
    data: {
      threadsAffected: number;
      messagesMarkedAsRead: number;
    };
  }>;

  editChatMessage: (
    messageUid: string,
    payload: {
      message?: string;
      attachments?: Array<{
        fileName: string;
        filePath: string;
        mimeType: string;
        publicUrl: string;
        fileSize?: number;
      }>;
      mentions?: Array<{
        uid: string;
        name: string;
      }>;
    }
  ) => Promise<any>;

  deleteChatMessage: (messageUid: string) => Promise<any>;
  addChatParticipants: (threadUid: string, participants: string[]) => Promise<any>;
  removeChatParticipants: (threadUid: string, participantUids: string[]) => Promise<any>;
  updateChatThread: (threadUid: string, payload: {
    subject?: string;
    status?: 'ACTIVE' | 'ARCHIVED' | 'CLOSED';
  }) => Promise<any>;
  archiveChatThread: (threadUid: string) => Promise<any>;
  createIntegrationConfig:(teamUid:string,payload:any)=>Promise<any>;
  getIntegrationConfig:(teamUid:string)=>Promise<any>;
  deleteIntegrationConfig:(id:string)=>Promise<any>;
  updateIntegrationConfig:(id:string,teamUid:string,payload:any)=>Promise<any>;
  
  // Escalation Chat APIs
  getOrCreateEscalationChatThread: (transactionId: string, stepUid: string) => Promise<any>;
  createEscalationChatThread: (payload: {
    transactionId: string;
    stepUid: string;
    subject?: string;
    participants?: string[];
    initialMessage?: string;
    escalationData?: any;
  }) => Promise<any>;

  // Chat File & Mention APIs
  uploadChatFiles: (files: File[]) => Promise<any>;
  getSignedDownloadUrl: (filePath: string, fileName: string) => Promise<any>;

  // Global chat threads (cross-transaction)
  getUserChatThreads: (page?: number, limit?: number) => Promise<any>;
  checkExistingChatThread: (transactionId: string, participants: string[]) => Promise<any>;

  // Chat Group APIs
  getChatGroups: (transactionId: string) => Promise<any>;
  createChatGroup: (transactionId: string, payload: { name: string; members: string[] }) => Promise<any>;
  updateChatGroup: (groupUid: string, payload: { name?: string; members?: string[] }) => Promise<any>;
  deleteChatGroup: (groupUid: string) => Promise<any>;
  getSuggestedChatGroups: (transactionId: string) => Promise<any>;

  getAllPendingCommunication:(payload:any, options?: { signal?: AbortSignal })=>Promise<any>;
  bulkUpdateEvents: (payload: any) => Promise<any>;
  getSignedUrl:(payload:any)=>Promise<any>;
  getTransactionAttachments: (uid: string) => Promise<any>;
  getAnalyticsEmbedUrl: () => Promise<any>;
  getConsolidatedHxH: (params?: { dateFrom?: string; dateTo?: string; process?: string;}) => Promise<any>;
  getTransactionEmbedUrl: (transactionKey: string) => Promise<any>;
  getSoftwareIssuesEmbedUrl: () => Promise<any>;
  getHelpEmbedUrl: () => Promise<any>;
  createChildTransactions: (uid: string, payload: any) => Promise<any>;

  getFlowStatusByTeam: (teamUid: string) => Promise<any>;

  // Detection Config APIs
  getDetectionConfig: (teamUid: string) => Promise<any>;
  getLocationTracking: (teamUid: string) => Promise<any>;
  getEscalationDefault: (teamUid: string) => Promise<any>;
  updateEscalationDefault: (teamUid: string, payload: any) => Promise<any>;
  updateLocationTracking: (teamUid: string, payload: any) => Promise<any>;
  createDetectionConfig: (teamUid: string, payload: any) => Promise<any>;
  updateDetectionConfig: (teamUid: string, configUid: string, payload: any) => Promise<any>;
  deleteDetectionConfig: (teamUid: string, configUid: string) => Promise<any>;

  // Verification Config APIs
  getVerificationConfig: (teamUid: string) => Promise<any>;
  createVerificationConfig: (teamUid: string, payload: any) => Promise<any>;
  updateVerificationConfig: (teamUid: string, configUid: string, payload: any) => Promise<any>;
  deleteVerificationConfig: (teamUid: string, configUid: string) => Promise<any>;

  // Default Steps Config APIs
  getDefaultSteps: (teamUid: string) => Promise<any>;
  createDefaultStep: (teamUid: string, payload: any) => Promise<any>;
  updateDefaultStep: (teamUid: string, stepUid: string, payload: any) => Promise<any>;
  deleteDefaultStep: (teamUid: string, stepUid: string) => Promise<any>;

  // Resource Image APIs
  uploadResourceImage: (resourceId: string, fieldName: string, formData: FormData) => Promise<any>;
  getResourceImage: (resourceId: string, fieldName: string) => Promise<any>;

  // Transaction Monitoring APIs
  getMonitoringConfigs: (teamUid: string) => Promise<any>;
  createMonitoringConfig: (teamUid: string, payload: any) => Promise<any>;
  updateMonitoringConfig: (teamUid: string, configUid: string, payload: any) => Promise<any>;
  deleteMonitoringConfig: (teamUid: string, configUid: string) => Promise<any>;
  getTransactionAlerts: (transactionUid: string) => Promise<any>;
  getTransactionRuleAlerts: (
    transactionUid: string,
    configUid: string,
    skip?: number,
    limit?: number
  ) => Promise<any>;
  getTeamAlerts: (teamUid: string) => Promise<any>;

  // Announcements APIs
  // Writes are Super-Admin-only and every read is audience-scoped server-side —
  // the UI gate is convenience, not the security boundary.
  createAnnouncement: (payload: CreateAnnouncementPayload) => Promise<any>;
  getAnnouncements: (params?: AnnouncementListParams) => Promise<any>;
  getAnnouncementByUid: (uid: string) => Promise<any>;
  // Who has viewed one announcement — Super Admin only. Returns { viewers, total }.
  getAnnouncementViews: (uid: string) => Promise<any>;
  // How many people a prospective process/role audience holds. Super Admin only, and it
  // runs the same resolution the save does, so a count it refuses is a save that would
  // also have been refused.
  getAnnouncementAudienceCount: (params: {
    client?: string;
    process?: string;
    roles?: string[];
  }) => Promise<any>;
  updateAnnouncement: (uid: string, payload: UpdateAnnouncementPayload) => Promise<any>;
  deleteAnnouncement: (uid: string) => Promise<any>;
  getAnnouncementVisibility: () => Promise<any>;
  markAnnouncementRead: (uid: string) => Promise<any>;
  markAllAnnouncementsRead: () => Promise<any>;

  // Legal & Consent APIs (PRIVACY_CONSENT_API.md). The admin routes are Super Admin
  // only server-side — getLegalAccess is the UI gate, not the security boundary.
  getLegalAccess: () => Promise<any>;
  getPublicLegalDocument: (slug: LegalSlug, params: { domain?: string; hash?: string }) => Promise<any>;
  getPublicLegalDocumentByUid: (uid: string) => Promise<any>;
  getLegalDocument: (slug: LegalSlug) => Promise<any>;
  getClientLegal: (clientUid: string) => Promise<any>;
  updateClientLegalConsent: (clientUid: string, payload: LegalConsentSettings) => Promise<any>;
  publishClientLegalDocument: (clientUid: string, slug: LegalSlug, payload: PublishLegalDocumentPayload) => Promise<any>;
  getClientLegalDocumentVersion: (clientUid: string, slug: LegalSlug, version: number) => Promise<any>;
  getConsentReport: (clientUid: string, params?: ConsentReportParams) => Promise<any>;
  signOutUnconsentedUsers: (clientUid: string) => Promise<any>;
  getResourceConsent: (resourceDataUid: string) => Promise<any>;
  recordResourceSmsConsent: (resourceDataUid: string, payload: RecordSmsConsentPayload) => Promise<any>;
};

export enum CallStatus {
  INITIATING = 'INITIATING',
  CALLING = 'CALLING',
  RECEIVED = 'RECEIVED',
  RECEIVING = 'RECEIVING',
  IN_PROGRESS = 'IN_PROGRESS',
  MISSED_CALL = 'MISSED_CALL',
  MANUAL_DISCONNECT = 'MANUAL_DISCONNECT',
  COMPLETED = 'COMPLETED',
  SUPPORT_JOINED = 'SUPPORT_JOINED',
  USER_JOINED = 'USER_JOINED',
  USER_MUTE = 'USER_MUTE',
  USER_UNMUTE = 'USER_UNMUTE',
  USER_CAMERA_SHOW = 'USER_CAMERA_SHOW',
  USER_CAMERA_HIDE = 'USER_CAMERA_HIDE',
  SUPPORT_MUTE = 'SUPPORT_MUTE',
  SUPPORT_UNMUTE = 'SUPPORT_UNMUTE',
  USER_LEFT = 'USER_LEFT',
  SUPPORT_LEFT = 'SUPPORT_LEFT',
  SUPPORT_SCREENSHOT = 'SUPPORT_SCREENSHOT',
  SUPPORT_LOCATION_REQUEST = 'SUPPORT_LOCATION_REQUEST',
  USER_LOCATION_UPDATE = 'USER_LOCATION_UPDATE',
  SUPPORT_RECORD_START = 'SUPPORT_RECORD_START',
  SUPPORT_RECORD_STOP = 'SUPPORT_RECORD_STOP'
}
export interface ISession {
  channelName: string;
  uid: string;
  appId: string;
  certificate: string;
  joinees: string[];
  keyId: string;
  tokens: {
    uid: string;
    token: string;
    uidNumber: string;
  }[];
}

export enum InputComponents {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  FILE = 'FILE',
  PASSWORD = 'PASSWORD',
  SELECT = 'SELECT',
  CHECKBOX = 'CHECKBOX',
  RADIO = 'RADIO',
  CAMERA = 'CAMERA',
  FORM = 'FORM',
  DATE = 'DATE',
  ESIGN = 'ESIGN',
  TEXTAREA = 'TEXTAREA',
  SWITCH = 'SWITCH',
  RESOURCE = 'RESOURCE'
}
export interface ICallPayload {
  key: string;
  keyUid: string;
  from: string;
  fromName: string;
  transaction: string;
  userMuted: boolean;
  userHidden: boolean;
  supportMuted: boolean;
  supportAccepted: string;
  to: {
    user: string;
    status: CallStatus;
  }[];
  status: CallStatus;
  history: { status: CallStatus; datetime: string; user: string }[];
  initiated: number;
  // Set by the callee's device the moment it actually raises its incoming-call
  // UI. Absent means "we sent the call but have no confirmation it reached a
  // device", which is why the caller shows "Calling..." until this arrives.
  ringingAt?: number;
  session?: ISession;
  screenshots: {
    file_name: string;
    file_path: string;
    mime_type: string;
    public_url: string;
    url: string;
  }[];
  initGetLocation: boolean;
  isRecording: boolean;
  recipients?: string[];
  sessionType: string;
  stepUid?: string; // For escalation chats - the step that triggered the escalation
  callDirection?: 'INBOUND' | 'OUTBOUND'; // INBOUND = dashboard → app (new direction)
}

export interface IMessage {
  fromName: string;
  uid: string;
  message: string;
  createdAt: number;
  readBy: string[];
}

export type FirebaseContextType = {
  app: FirebaseApp | null;
  database: Database | null;
  firebaseUid: string | null;
  addCallData: (data: any, txnId: string) => Promise<string>;
  getCallData: (txnId: string) => Promise<ICallPayload>;
  updateCallStatus: (callId: string, newStatus: CallStatus) => Promise<any>;
  addCallHistoryEntry: (callId: string, history: any) => Promise<any>;
  getMyOngoingCalls: (userUid: string) => Promise<any>;
  updateSessionData: (callId: string, sessionData: any) => Promise<any>;
  addCallToUser: (callId: string, userId: string, status: CallStatus, userType: string) => void;
  addSessionUsers: (callId: string, userId: string) => void;
  updateMuteStatus: (callId: string, supportMuted: boolean) => void;
  addScreenshot: (callId: string, screenshot: any) => void;
  updateLocationStatus: (callId: string, status: boolean) => Promise<any>;
  updateRecordingStatus: (callId: string, isRecording: boolean) => Promise<any>;
  updateFromName: (callId: string, fromName: string) => Promise<any>;
  pushMessage: (callId: string, message: IMessage) => void;
  updateRecipients: (callId: string, recipients: string[]) => void;
  markMessagesAsRead: (callId: string, uid: string) => void;
  updateSessionTokens: (callId: string, token: { token: string; uid: string; uidNumber: string }) => void;
  removeUserUidFromTo: (callId: string, uid: string) => void;
  updateUserCallStatus: (callId: string, uid: string, newStatus: CallStatus) => void;
  updateCallSupportInitiatedBy: (callId: string, supportAccepted: string) => void;
  getMessageHistory:(callId:string)=>void;
  getLocationData:(txnId: string, userId: string)=>void;
  getAllUsersLocationData: (txnId: string) => Promise<Record<string, any> | null>;
  subscribeToTransactionLocations: (
    txnId: string,
    onUpdate: (data: Record<string, any> | null) => void
  ) => () => void;
  subscribeToUserActivity: (
    uids: string[],
    onUpdate: (data: Record<string, UserActivity>) => void
  ) => () => void;
};

/** One entry of `user-activity/{uid}` — the app's presence + battery heartbeat. */
export type UserActivity = {
  /** Epoch ms of the last heartbeat, or null if the user has never written one. */
  lastSeenAt: number | null;
  /** Battery percentage at that heartbeat, or null if unavailable. */
  battery: number | null;
  /**
   * Epoch ms the app marked this user logged out, or null. When set at/after
   * lastSeenAt it forces offline immediately on logout, while lastSeenAt +
   * battery are kept so "last seen" and battery still display. Cleared on the
   * next heartbeat after a re-login.
   */
  loggedOutAt: number | null;
  /**
   * Why the device is reachable, written alongside each heartbeat:
   *   - 'foreground' — app open on screen.
   *   - 'tracking'   — app backgrounded but live-tracking the trip (still online).
   *   - 'background' — app backgrounded and NOT tracking → shown offline, so a
   *     driver who merely has the app installed/alive does not read as online.
   * Absent for heartbeats from app builds predating this field; treated as online
   * (the prior behaviour) so an old client is never wrongly shown offline.
   */
  appState?: 'foreground' | 'tracking' | 'background' | null;
};

export type VideoSessionContextType = {
  callUid: string | null;
  setCallUid: (callUid: string) => void;
  session: ICallPayload | null;
  messages: IMessage[];
  client: IAgoraRTCClient | null;
  setClient: (client: IAgoraRTCClient | null) => void;
  localAudioTrack: any;
  setLocalAudioTrack: (track: any) => void;
  remoteVideo: IRemoteVideoTrack | null | undefined;
  setRemoteVideo: (video: IRemoteVideoTrack | null | undefined) => void;
  cleanupSession: () => void;
  forceCleanupSession: () => void;
  isClientReady: () => boolean;
  getClientState: () => {
    client: IAgoraRTCClient | null;
    audioTrack: any;
    remoteVideo: IRemoteVideoTrack | null | undefined;
    isConnected: boolean;
    sessionState: any;
  };
  updateSessionState: (updates: any) => void;
  reconnectVideoStreams: () => void;
};

export type ThemeContextType = {
    primaryColor: string;
    setPrimaryColor: (color: string) => void;
    setLogo: (color: string) => void;
    setLogoLight: (color: string) => void;
    setClientName: (color: string) => void;
    updateThemeData: (themeData: { name: string, color_palatte: any, icons: any }) => void;
    logo: string | null;
    logoLight: string | null;
    themeType: 'default' | 'client' | 'domain',
    setThemeType: (themeType: 'default' | 'client' | 'domain') => void;
}

export type ISupportUserListItem = {
  firstName: string;
  lastName: string;
  uid: string;
  role: string;
};

export interface FieldData {
  value: {
    files: any[]; // Replace `any` if you have a specific file type/interface
  };
  status: boolean;
  field: string;
  question: string;
  description: string;
  inputComponent: string;
  children: any[];
  _id: string;
  uid: string;
}
export interface IDashboardData {
  transaction: {
    total: number;
    videos: number;
    images: number;
    session: number;
    sign: number;
    missedCalls: number;
    filtered: {
      filter: {
        start: string; // ISO Date string
        end: string; // ISO Date string
      };
      data: {
        total: number;
        videos: number;
        images: number;
        session: number;
        sign: number;
        missedCalls: number;
      };
    };
  };
  users: {
    total: number;
    admins: number;
    anonymous: number;
    clientAdmin: number;
    teamManager: number;
    supportExecutive: number;
    fieldExecutive: number;
  };
  unmapped: {
    videos: number;
    images: number;
    sign: number;
  };
}

export enum IEventSource {
  WEB = 'WEB',
  APP = 'APP'
}

interface IBase {
  name: string;
  description: string;
  componentType?: string;
  formType?: string;
  minimumAnswers?: number;
  data?: any;
  actor: any;
}
export interface ISESSION extends IBase {
   onComplete: any;
   onCompleteStatusUid: string;
  actor: any;
  component: any;
  stepType: string;
  settings: {
    cameraMode?: 'environment' | 'front';
    callType?: 'audio' | 'video';
  };
}

export interface IVideo extends IBase {
  // notification:any
  onComplete:any
  onCompleteStatusUid: any;
  actor: any;
  component: any;
  stepType: string;
  settings: {
    maxSize?: number;
    minDuration?: number;
    maxDuration?: number;
    watermark?: boolean;
    coordinate?: boolean;
    cameraMode?: 'environment' | 'front';
  };
}

export interface IMaps extends IBase {
     onComplete: any;
   onCompleteStatusUid: string;
   locationTrackingStep:any


  actor: any;
  component: any;
  title: string;
  description: string;
  stepType: string;
}

export interface ISign extends IBase {
  onComplete: any;
  onCompleteStatusUid: string;
  actor: any;
  component: any;
  title: string;
  description: string;
  settings: {
    maxSize: number;
    width?: number;
    height?: number;
  };
  stepType: string;
}
export interface IImages extends IBase {
   onComplete: any;
   onCompleteStatusUid: string;
  name: string;
  data?: any;
  component: any;
  title: string;
  actor: any;

  fields: {
    inputComponent: string; // Changed from inputType to inputComponent
    
    question: string;
    description: string;
  }[];
  settings: {
    maxSize?: number;
    width?: number;
    height?: number;
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
    mimeType?: string[];
    watermark?: boolean;
    coordinate?: boolean;
    cameraMode?: string;
  };
}

export interface IDOCUMENT extends IBase {
  onComplete: any;
   onCompleteStatusUid: string; // UID of the status to be set on completion
  actor: any;
  component: any;
  stepType: string;
  title: string;
  description: string;
  settings: {
    maxSize: number;
    mimeType?: string[];
    watermark?: boolean;
    coordinate?: boolean;
  };
}

export interface IFORM extends IBase {
  component: React.ComponentType<any>;
}

export interface IINSTRUCTIONS extends IBase {
  onComplete: any;
  onCompleteStatusUid: string;
  actor: any;
  component: any;
  /**
   * Fields the user answers alongside the instruction. Same shape as a FORM
   * step's fields — they live on `component.fields` once saved; this mirrors
   * them at the top level while the form is open, as Formik edits them there.
   */
  fields?: any[];
  stepType: string;
  title: string;
  description: string;
  settings: {
    instruction: string;
    isEsignRequired?: boolean;
    uploadedDocument?: string;
    // Which of the three content modes this instruction step presents. Absent on legacy
    // steps — the UI/display then derives it from whichever content is present.
    contentType?: 'TEXT' | 'PDF' | 'ATTACHMENT';
    // ATTACHMENT mode: require the end user to view the attachment (watch the video to the
    // end / complete the demo) before the step's Continue button is enabled. Absent is
    // treated as true (required) to preserve the original always-gated behaviour.
    requireView?: boolean;
    supportfiles?: Array<{
      file_name: string;
      file_path: string;
      mime_type: string;
      public_url: string;
      question?: string;
      description?: string;
    }>;
    files?: Array<{
      file_name: string;
      file_path: string;
      mime_type: string;
      public_url: string;
      question?: string;
      description?: string;
    }>;
    // A media attachment entered as a link (same shape as an announcement attachment),
    // used for the 'ATTACHMENT' content mode — typically a single video the user watches.
    attachments?: AnnouncementAttachmentInput[];
  };
}

// ==============================|| TYPES - USER DEVICE ||============================== //

export interface UserDevice {
  user: string; // ObjectId ref to Users
  fcmToken: string;
  deviceId?: string;
  deviceType?: string;
  deviceModel?: string;
  osVersion?: string;
  appVersion?: string;
  browserName?: string;
  browserVersion?: string;
  isActive: boolean; // default: true
  lastUsed: Date; // default: Date.now
}

export interface DeviceRegistrationPayload {
  fcmToken: string;
  deviceId: string;
  deviceType: string;
  deviceModel: string;
  osVersion: string;
  appVersion: string;
  browserName: string;
  browserVersion: string;
}
